import {
    Button,
    Col,
    Input,
    Modal,
    Row,
    Table,
    Typography,
    Tooltip,
    notification,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { FaCommentAlt } from 'react-icons/fa';
import moment from 'moment';
import { IMRRTransaction } from '../../Invoices/context/invoices.interface';
import {
    getMRRTransactions,
} from '../../../api/payments';
import { OCTOPUS_BASE_URL } from '../../../api';
import AddTransaction from './AddTransaction';
import Businesses from './Businesses';
import { formatCurrency } from '../../../utils/numberFormatter';

const { Text } = Typography;

const MRRTransactions = ({ invoice, onClose }: any) => {
    const [loading, seLoading] = useState<boolean>(false);
    const [transactions, setTransactions] = useState<any[]>([]);
    const [newTransaction, setNewTransaction] = useState<boolean>(false);
    const [transactionToUpdate, setTransactionToUpdate] = useState<IMRRTransaction | null>(null);

    const allTransactions = useRef<IMRRTransaction[]>([]);

    useEffect(() => {
        if (!invoice) return;
        seLoading(true);
        getMRRTransactions(invoice._id)
            .then(({ data }: any) => {
                allTransactions.current = data.data.data;
                setTransactions(data.data.data);
            })
            .catch((error: any) => {
                const description = error?.response?.data?.message || 'Error fetching transactions';
                notification.error({
                    message: 'Error',
                    description,
                });
            })
            .finally(() => seLoading(false));
    }, [invoice]);

    const isOpen = invoice;

    const columns = [
        {
            title: 'Id',
            dataIndex: 'transactionId',
            key: 'transactionId',
            width: 100,
            align: 'center' as 'center',
        },
        {
            title: 'Manual',
            dataIndex: 'isManual',
            key: 'isManual',
            width: 100,
            align: 'center' as 'center',
            render: (isManual: boolean) => (isManual ? <Text type="warning">YES</Text> : 'NO'),
        },
        {
            title: 'Total business',
            dataIndex: 'business',
            key: 'business',
            width: 124,
            align: 'center' as 'center',
            render: (item: string[]) => item.length || 0,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 156,
            align: 'center' as 'center',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 200,
            align: 'center' as 'center',
            render: (date: string) => moment(date).format('DD/MM/YYYY HH:mm'),
        },
        {
            title: 'Payment Method',
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            width: 200,
            align: 'center' as 'center',
        },
        {
            title: 'Currency',
            dataIndex: 'currency',
            key: 'currency',
            width: 100,
            align: 'center' as 'center',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 140,
            align: 'center' as 'center',
            render: (amount: number) => formatCurrency(amount),
        },
        {
            title: 'Comment',
            dataIndex: 'comment',
            key: 'comment',
            align: 'center' as 'center',
            width: 100,
            render: (comment: string) => comment && (
                <Tooltip title={comment}>
                    <FaCommentAlt />
                </Tooltip>
            ),
        },
        // {
        //     title: 'Actions',
        //     dataIndex: '_id',
        //     key: 'actions',
        //     align: 'center' as 'center',
        //     width: 100,
        //     render: (transactionId: any, transaction: IMRRTransaction) => (
        //         <Button
        //             type="link"
        //             style={{ padding: 0 }}
        //             onClick={() => setTransactionToUpdate(transaction)}
        //         >
        //             <BiEdit size={20} />
        //         </Button>
        //     ),
        // },
    ];

    const handleDownload = () => {
        const token = localStorage.getItem('token');
        const invoiceId = invoice?._id;
        const utc = (moment().utcOffset() / 60).toString();
        window.open(`${OCTOPUS_BASE_URL}/reports?CSVtype=mrrTransactions&invoiceId=${invoiceId}&token=${token}&utc=${utc}`, '_blank');
    };

    const handleClose = () => {
        const amount = transactions.reduce((acc, transaction) => acc + transaction.amount, 0);
        const newInvoice = {
            ...invoice,
            amount,
        };
        onClose(newInvoice);
    };

    const handleSearch = (value: string) => {
        if (!value) {
            return setTransactions(allTransactions.current);
        }
        const filtered = allTransactions.current.filter(
            (transaction) => transaction.transactionId.toString().includes(value) || transaction.transactionId.toString().includes(value),
        );
        setTransactions(filtered);
    };

    return (
        <Modal
            title={`MRR Transactions of invoice: ${invoice?.invoiceId || ''}`}
            open={isOpen}
            width="100%"
            centered
            onOk={handleClose}
            onCancel={handleClose}
            cancelButtonProps={{
                style: {
                    display: 'none',
                },
            }}
        >
            {
                newTransaction && (
                    <AddTransaction
                        visible={newTransaction}
                        invoice={invoice}
                        onCancel={(transaction: any) => {
                            if (transaction) {
                                setTransactions((transactions: any) => [...transactions, transaction]);
                            }
                            setNewTransaction(false);
                        }}
                    />
                )
            }
            <Row justify="end" gutter={[10, 20]}>
                <Col>
                    <Input.Search
                        placeholder="Transaction"
                        onSearch={handleSearch}
                    />
                </Col>
                <Col>
                    <Button
                        type="primary"
                        onClick={handleDownload}
                    >
                        Download
                    </Button>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        onClick={() => setNewTransaction(true)}
                    >
                        Add transaction
                    </Button>
                </Col>
                <Col span={24}>
                    <Table
                        loading={loading}
                        columns={columns}
                        dataSource={transactions}
                        bordered
                        scroll={{ x: 1000 }}
                        rowKey="_id"
                        size="small"
                        expandable={{
                            rowExpandable: (record) => !!record.business.length,
                            expandedRowRender: (record) => (
                                <Businesses
                                    invoiceId={record.invoiceID}
                                    businessIds={record.business}
                                />
                            ),
                        }}
                        summary={(pageData) => {
                            let total = 0;

                            pageData.forEach(({ amount }: any) => {
                                total += amount;
                            });

                            return (
                                <Table.Summary fixed>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={1} />
                                        <Table.Summary.Cell index={2} align="center">
                                            Total
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={3} />
                                        <Table.Summary.Cell index={4} />
                                        <Table.Summary.Cell index={5} />
                                        <Table.Summary.Cell index={6} />
                                        <Table.Summary.Cell index={7} />
                                        <Table.Summary.Cell index={8} />
                                        <Table.Summary.Cell index={9} align="center">
                                            <Text
                                                strong
                                                type={total < 0 ? 'danger' : 'success'}
                                            >
                                                {formatCurrency(total)}
                                            </Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={10} />
                                    </Table.Summary.Row>
                                </Table.Summary>
                            );
                        }}
                    />
                </Col>
            </Row>
        </Modal>
    );
};

export default MRRTransactions;
